/**
 * Organizations, memberships, spaces and the venue directory.
 *
 * Mirrors the Task 5 and Task 6 engine endpoints, all of which sit behind
 * authenticate + requireAdmin. Nothing in this file is a permission decision — it is an
 * HTTP client. The engine authorizes every one of these calls again.
 */

import { api } from './apiClient';

// ── Organizations (task 5) ──────────────────────────────────────────────────

export interface Organization {
  id: string;
  name: string;
  slug: string;
  status: string;
  memberCount: number;
  createdAt: string;
}

export const listOrganizations = () => api.get<Organization[]>('/admin/organizations');
export const getOrganization = (id: string) => api.get<Organization>(`/admin/organizations/${id}`);
export const createOrganization = (body: { name: string; slug?: string }) =>
  api.post<Organization>('/admin/organizations', body);
export const updateOrganization = (id: string, body: { name?: string; slug?: string; status?: string }) =>
  api.patch<Organization>(`/admin/organizations/${id}`, body);

// ── Memberships (task 5) ────────────────────────────────────────────────────

/**
 * Mirrors the engine's memberships_roles_allowed_ck.
 *
 * A ROLE HERE IS AN IDENTITY CLAIM, NOT AN ELIGIBILITY ONE. Granting 'investor' says who
 * somebody is; it approves nothing. Accreditation, sophistication and the 506(b) network
 * are separate records behind separate gates, and no membership touches them.
 */
export const MEMBERSHIP_ROLES = [
  'admin', 'organizer', 'artist', 'venue', 'sponsor', 'agency', 'investor',
  'staff', 'buyer', 'organization_admin', 'venue_manager',
] as const;
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number];

export const SCOPE_TYPES = ['platform', 'organization', 'venue', 'event'] as const;
export type ScopeType = (typeof SCOPE_TYPES)[number];

/** 'admin' is platform-scope only — the engine enforces it, this mirrors the rule. */
export const rolesForScope = (scope: ScopeType): readonly MembershipRole[] =>
  scope === 'platform' ? MEMBERSHIP_ROLES : MEMBERSHIP_ROLES.filter((r) => r !== 'admin');

export interface Membership {
  id: string;
  userId: string;
  userEmail?: string | null;
  userName?: string | null;
  scopeType: ScopeType;
  scopeId: string | null;
  roles: MembershipRole[];
  status: string;
  createdAt: string;
  revokedAt?: string | null;
}

/**
 * `status` defaults to 'active' on the engine. Pass 'revoked' to see what was taken away —
 * a revoked grant is history worth showing, not a row to hide.
 */
export const listMemberships = (
  query: { userId?: string; scopeType?: string; scopeId?: string; status?: string } = {},
) => {
  const q = new URLSearchParams(Object.entries(query).filter(([, v]) => v) as [string, string][]);
  const qs = q.toString();
  return api.get<Membership[]>(`/admin/memberships${qs ? `?${qs}` : ''}`);
};

export const grantMembership = (body: {
  userId: string; scopeType: ScopeType; scopeId?: string | null; roles: MembershipRole[];
}) => api.post<Membership>('/admin/memberships', body);

export const revokeMembership = (id: string) => api.delete<{ id: string }>(`/admin/memberships/${id}`);

export interface EffectiveAccess {
  roles: string[];
  legacyRole: string | null;
  viaMembership: string[];
  isPlatformAdmin: boolean;
  memberships?: Membership[];
}

export const getUserAccess = (userId: string) =>
  api.get<EffectiveAccess>(`/admin/users/${userId}/access`);

// ── Spaces (task 6) ─────────────────────────────────────────────────────────

export type VenueKind = 'venue_profile' | 'managed_venue';
export type OccupancyMode = 'admissions_only' | 'physical_occupancy';

export interface SpacePolicy {
  publicEventRequiresVoleto: boolean;
  allowPrivateEventExemption: boolean;
  occupancyMode: OccupancyMode;
  defaultRsvpCap: number | null;
}

export interface Space {
  id: string;
  name: string;
  slug: string | null;
  status: string;
  capacity: number | null;
  venueKind: VenueKind;
  venueId: string;
  venueName?: string | null;
  organizationId: string | null;
  /** Absent only on a projection that did not select the policy join. */
  policy?: SpacePolicy;
  createdAt?: string;
}

export const listSpaces = (query: { organizationId?: string | null; venueKind?: string; venueId?: string; status?: string } = {}) => {
  const q = new URLSearchParams(Object.entries(query).filter(([, v]) => v) as [string, string][]);
  const qs = q.toString();
  return api.get<Space[]>(`/admin/spaces${qs ? `?${qs}` : ''}`);
};

export const getSpace = (id: string) => api.get<Space>(`/admin/spaces/${id}`);

export const createSpace = (body: {
  venueKind: VenueKind; venueId: string; name: string;
  capacity?: number | null; organizationId?: string | null;
}) => api.post<Space>('/admin/spaces', body);

export const updateSpace = (id: string, body: { name?: string; capacity?: number | null; status?: string }) =>
  api.patch<Space>(`/admin/spaces/${id}`, body);

export const updateSpacePolicy = (id: string, body: Partial<SpacePolicy>) =>
  api.patch<Space>(`/admin/spaces/${id}/policy`, body);

/** Event -> Space -> Venue -> Organization, resolved by the engine. Ids only, never names. */
export interface EventScope {
  eventId: string;
  organizerId: string;
  spaceId: string | null;
  venueKind: VenueKind | null;
  venueId: string | null;
  organizationId: string | null;
  viaSpace: boolean;
}

export const getEventScope = (eventId: string) =>
  api.get<EventScope>(`/admin/events/${eventId}/scope`);

// ── Venue directory (task 7) ────────────────────────────────────────────────

export interface DirectoryVenue {
  venueKind: VenueKind;
  venueId: string;
  name: string;
  city: string | null;
  state: string | null;
  capacity: number | null;
  organizationId: string | null;
  isPublic: boolean | null;
  approved: boolean | null;
}

/**
 * Every venue an admin can act on, across both venue systems.
 *
 * The public managed-venue list filters is_public = TRUE, which hides privately operated
 * venues from the console that administers them. This endpoint does not.
 */
export const listVenueDirectory = (query: { organizationId?: string | null; venueKind?: string } = {}) => {
  const q = new URLSearchParams(Object.entries(query).filter(([, v]) => v) as [string, string][]);
  const qs = q.toString();
  return api.get<DirectoryVenue[]>(`/admin/venue-directory${qs ? `?${qs}` : ''}`);
};
